import "./TestEntry.scss";

import {
  AmslerGridTestEntry,
  ColorVisionTestEntry,
  ContrastSensitivityTestEntry,
  CoverTestEntry,
  DyscalculiaTestEntry,
  ExtraTestEntryHeader,
  FDTScreeningTestEntry,
  OcularMotilityTestEntry,
  PupillometryTestEntry,
  StereoAcuityTestEntry,
  VAcuityTestEntry,
  VideonystagmographyTestEntry,
  WCSTTestEntry,
  NPCTTestEntry,
  OptokineticNystagmusTestEntry,
} from "@components/testContainer";
import { Box, Stack } from "@mantine/core";
import { TestCategory, TestResult } from "types/testTypes";

import ContinuousPerformanceTestEntry from "../new_test_ui/new_test_steps/test-configuration/extra-tests/continuousPerformance/ContinuousPerformanceTestEntry";
import DyslexiaTestEntry from "../new_test_ui/new_test_steps/test-configuration/extra-tests/dyslexia/DyslexiaTestEntry";
import KineticPerimetryTestEntry from "@components/new_test_ui/new_test_steps/test-configuration/extra-tests/kinetic/KineticPerimetryTestEntry";
import MonocularTestEntry from "./MonocularTestEntry";
import OrganizationDetailView from "./SimpleTestEntry/OrganizationDetailView";
import ReadingAssessmentTestEntry from "../new_test_ui/new_test_steps/test-configuration/extra-tests/readingAssessment/ReadingAssessmentTestEntry";
import { SimpleTestEntry } from "./SimpleTestEntry";
import { useMemo } from "react";
import { Printable, usePrintableContext } from "@components";
import { isBinocularVFATestType } from "@constants/testConstants/testSpecificConstants";
import { selectShowOrgDetailInResults } from "@store/ui/uiSelectors";
import { useSelector } from "react-redux";

export interface TestEntryProps {
  entry: TestResult | null;
}

const TEST_COMPONENT_MAP = {
  [TestCategory.VisualField]: null, // Special case handled separately
  [TestCategory.VisualAcuity]: VAcuityTestEntry,
  [TestCategory.ContrastSensitivity]: ContrastSensitivityTestEntry,
  [TestCategory.ColorVision]: ColorVisionTestEntry,
  [TestCategory.AmslerGrid]: AmslerGridTestEntry,
  [TestCategory.FDT]: FDTScreeningTestEntry,
  [TestCategory.Pupillometry]: PupillometryTestEntry,
  [TestCategory.OcularMotility]: OcularMotilityTestEntry,
  [TestCategory.Dyslexia]: DyslexiaTestEntry,
  [TestCategory.ReadingAssessment]: ReadingAssessmentTestEntry,
  [TestCategory.ContinuousPerformance]: ContinuousPerformanceTestEntry,
  [TestCategory.Cover]: CoverTestEntry,
  [TestCategory.StereoAcuity]: StereoAcuityTestEntry,
  [TestCategory.Videonystagmography]: VideonystagmographyTestEntry,
  [TestCategory.Kinetic]: KineticPerimetryTestEntry,
  [TestCategory.Dyscalculia]: DyscalculiaTestEntry,
  [TestCategory.WisconsinCardSorting]: WCSTTestEntry,
  [TestCategory.BinocularVision]: NPCTTestEntry,
  [TestCategory.OptokineticNystagmus]: OptokineticNystagmusTestEntry,
};

const isSimpleTestType = (testType) => {
  if (!testType) {
    return false;
  }

  return (
    isBinocularVFATestType(testType) ||
    testType.startsWith("SUPERIOR") ||
    ["C-40", "C-76", "FF-120"].includes(testType)
  );
};

export default function TestEntry({ entry, ...props }) {
  const { prettier } = usePrintableContext();
  const shouldShowOrgDetail = useSelector(selectShowOrgDetailInResults);

  const simple = useMemo(
    () => (entry?.testType ? isSimpleTestType(entry.testType) : undefined),
    [entry?.testType],
  );

  if (!entry) {
    return null;
  }

  if (entry.category === TestCategory.VisualField || !entry.category) {
    if (simple === undefined) {
      return null;
    }
    const orgDetail =
      entry.orgId && shouldShowOrgDetail ? (
        <OrganizationDetailView organization={null} organizationId={entry.orgId} compact />
      ) : null;
    return (
      <Printable.Section floatingLogo={!simple} orgDetail={orgDetail}>
        {simple ? (
          <SimpleTestEntry entry={entry} {...props} />
        ) : (
          <MonocularTestEntry entry={entry} {...props} />
        )}
      </Printable.Section>
    );
  }

  const TestComponent = TEST_COMPONENT_MAP[entry.category];
  if (!TestComponent) return null;

  const header = <ExtraTestEntryHeader entry={entry} onPatientChanged={props.onPatientChanged} />;

  if ((TestComponent as { selfSections?: boolean }).selfSections) {
    return <TestComponent entry={entry} header={header} {...props} />;
  }

  return (
    <Printable.Section>
      <Stack>
        {header}
        <Box mx={prettier ? 0 : "xl"}>
          <TestComponent entry={entry} {...props} />
        </Box>
      </Stack>
    </Printable.Section>
  );
}
